/**
 * Captura de microfono del kiosco.
 *
 * Produce EXACTAMENTE lo que espera `backend/voz/stt.py`: **PCM de 16 kHz,
 * mono, 16 bits con signo, little-endian**. El navegador casi nunca graba a
 * 16 kHz (lo normal son 44,1 o 48 kHz), asi que aqui se remuestrea a mano:
 * si el kiosco mandara 48 kHz, faster-whisper oiria al jefe a camara lenta.
 *
 * Cadena de audio::
 *
 *   getUserMedia -> MediaStreamAudioSourceNode -> AudioWorklet ("captura-pcm")
 *        -> remuestreo lineal a 16 kHz -> Int16 -> fragmentos de 100 ms
 *
 * El AudioWorklet corre en el hilo de audio (no se corta cuando React pinta).
 * Si el navegador no lo soporta se cae a `ScriptProcessorNode`, obsoleto pero
 * universal: mejor un poco de jitter que un kiosco sordo.
 *
 * Ademas de trocear, este modulo decide **cuando ha terminado el turno**: mide
 * el volumen (RMS) sobre una ventana y, tras un silencio sostenido, avisa con
 * `alSilencio` para que el llamante mande `{"tipo":"fin_turno"}`. El usuario
 * tiene 88 anos: no puede depender de pulsar un boton para dejar de hablar.
 *
 * AISLAMIENTO: este modulo solo mueve bytes. No interpreta el audio, no decide
 * modos y no habla con el agente. Lo que se dicta jamas es una orden.
 */

// ---------------------------------------------------------------------------
// Constantes del contrato con el backend
// ---------------------------------------------------------------------------

/** Frecuencia de muestreo que exige el STT. Cambiarla rompe la transcripcion. */
export const FRECUENCIA_DESTINO = 16_000;

/** Muestras por fragmento enviado: 1.600 a 16 kHz = 100 ms (3.200 bytes). */
const MUESTRAS_POR_FRAGMENTO = 1_600;

/** Tamano de bloque del ScriptProcessor de respaldo (~85 ms a 48 kHz). */
const BLOQUE_RESPALDO = 4_096;

/** Cada cuanto se refresca el indicador de volumen de la pantalla (ms). */
const MS_ENTRE_NIVELES = 60;

/** Nombre del procesador registrado en el AudioWorklet. */
const NOMBRE_WORKLET = 'captura-pcm-androcles';

// ---------------------------------------------------------------------------
// Tipos publicos
// ---------------------------------------------------------------------------

/** Estado de la captura, para pintar el indicador de escucha. */
export type EstadoCaptura = 'parada' | 'iniciando' | 'escuchando' | 'error';

/**
 * Motivo por el que no se puede grabar. Los tres primeros son los fallos
 * reales del kiosco; los demas existen para no mentir con un "desconocido".
 */
export type MotivoFalloAudio =
  | 'permiso_denegado'
  | 'sin_microfono'
  | 'contexto_inseguro'
  | 'microfono_ocupado'
  | 'navegador_sin_soporte'
  | 'desconocido';

/** Recibe un fragmento de PCM 16 kHz mono 16 bits listo para el WebSocket. */
export type AlFragmento = (pcm: ArrayBuffer) => void;

/** Se dispara cuando el jefe lleva callado el tiempo configurado. */
export type AlSilencio = () => void;

/** Volumen normalizado 0..1 para el indicador de escucha. */
export type AlNivel = (nivel: number) => void;

/** Ajustes opcionales de la captura. Los valores por defecto son los buenos. */
export interface OpcionesCaptura {
  /** Volumen para el indicador visual (ya suavizado y limitado a ~16 Hz). */
  alNivel?: AlNivel;
  /** Fallo sobrevenido con el microfono ya abierto (se desenchufa, p. ej.). */
  alFallo?: (error: ErrorAudio) => void;
  /** Silencio sostenido que cierra el turno. Por defecto 1.200 ms. */
  msSilencioFinTurno?: number;
  /** Voz minima antes de considerar que hay turno. Por defecto 250 ms. */
  msVozMinima?: number;
  /** Suelo absoluto del umbral RMS. Por defecto 0,010. */
  umbralMinimo?: number;
  /**
   * Tope duro de voz acumulada antes de cerrar el turno. Por defecto 20.000 ms.
   *
   * Con ruido de fondo constante puede no llegar nunca una pausa clara; sin
   * este tope el microfono se queda abierto y Whisper acaba inventandose
   * frases para rellenar el silencio.
   */
  msMaximoTurno?: number;
}

/** Texto para el usuario: un titular y que hacer. Cero jerga tecnica. */
export interface MensajeAmable {
  titulo: string;
  consejo: string;
  /**
   * `true` si el aviso habla de la CONEXION y no del microfono. El kiosco lo
   * retira solo en cuanto vuelve a haber linea, sin que el jefe toque nada.
   */
  esDeLinea?: boolean;
}

/**
 * Textos de fallo escritos para un usuario de 88 anos.
 *
 * Regla: nombrar el objeto fisico o el gesto ("el candado de arriba", "el
 * cable"), nunca la causa tecnica, y recordar SIEMPRE que se puede escribir:
 * la voz caida no deja mudo al kiosco.
 */
const MENSAJES: Record<MotivoFalloAudio, MensajeAmable> = {
  contexto_inseguro: {
    titulo: 'No puedo encender el microfono desde esta direccion.',
    consejo:
      'Ha entrado por una direccion que el navegador no considera de confianza y por eso ' +
      'no me deja oirle. Cierre esta ventana y vuelva a abrir Androcles con el acceso ' +
      'directo del escritorio. Si aun asi no le oigo, avise a Ricardo. ' +
      'Mientras tanto, escribame aqui abajo y le tomo nota igual.',
  },
  permiso_denegado: {
    titulo: 'No me han dejado usar el microfono.',
    consejo:
      'Arriba del todo, a la izquierda de la direccion, hay un candado pequeno. ' +
      'Pulselo y elija «Permitir microfono». Despues vuelva a darle a «Empezar a dictar». ' +
      'Si prefiere, escribame aqui abajo y le tomo nota igual.',
  },
  sin_microfono: {
    titulo: 'No encuentro ningun microfono.',
    consejo:
      'Compruebe que el microfono esta enchufado y encendido. ' +
      'Mientras tanto, escribame aqui abajo y le tomo nota igual.',
  },
  microfono_ocupado: {
    titulo: 'El microfono lo esta usando otro programa.',
    consejo:
      'Cierre el otro programa que este usando el microfono y vuelva a intentarlo. ' +
      'Mientras tanto, escribame aqui abajo y le tomo nota igual.',
  },
  navegador_sin_soporte: {
    titulo: 'Este navegador no sabe grabar.',
    consejo:
      'Avise a Ricardo para que abra Androcles con el navegador de siempre. ' +
      'Mientras tanto, escribame aqui abajo y le tomo nota igual.',
  },
  desconocido: {
    titulo: 'No he conseguido encender el microfono.',
    consejo:
      'Pruebe otra vez dentro de un momento. ' +
      'Mientras tanto, escribame aqui abajo y le tomo nota igual.',
  },
};

/** Fallo de captura con mensaje ya traducido a lenguaje de persona. */
export class ErrorAudio extends Error {
  constructor(
    public readonly motivo: MotivoFalloAudio,
    /** Excepcion original del navegador; solo para la consola, nunca para el jefe. */
    public readonly causa?: unknown,
  ) {
    super(MENSAJES[motivo].titulo);
    this.name = 'ErrorAudio';
  }

  /** Titular corto y grande. */
  get titulo(): string {
    return MENSAJES[this.motivo].titulo;
  }

  /** Que tiene que hacer el usuario, paso a paso. */
  get consejo(): string {
    return MENSAJES[this.motivo].consejo;
  }
}

// ---------------------------------------------------------------------------
// Diagnostico previo (antes de pedir nada al usuario)
// ---------------------------------------------------------------------------

/**
 * Comprueba si el navegador puede grabar, SIN pedir permiso todavia.
 *
 * El caso que mas duele en este proyecto es `contexto_inseguro`: el kiosco se
 * sirve por HTTP en la LAN (`http://192.168.x.x:5300`) y ahi el navegador
 * borra `navigator.mediaDevices` entero. Hay que distinguirlo de un navegador
 * viejo, porque la solucion es distinta (entrar por la direccion buena).
 *
 * Returns:
 *     El motivo del fallo, o `null` si se puede intentar grabar.
 */
export function diagnosticarMicrofono(): MotivoFalloAudio | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'navegador_sin_soporte';
  }

  const dispositivos = navigator.mediaDevices as MediaDevices | undefined;
  const hayGetUserMedia = !!dispositivos && typeof dispositivos.getUserMedia === 'function';

  // `isSecureContext` es false en http:// por IP; en localhost es true.
  if (window.isSecureContext === false) return 'contexto_inseguro';
  if (!hayGetUserMedia) return 'navegador_sin_soporte';
  if (typeof AudioContext === 'undefined') return 'navegador_sin_soporte';

  return null;
}

/** Atajo booleano de `diagnosticarMicrofono`. */
export function capturaSoportada(): boolean {
  return diagnosticarMicrofono() === null;
}

/** Texto amable de un motivo, para pintarlo sin construir un `ErrorAudio`. */
export function mensajeDeMotivo(motivo: MotivoFalloAudio): MensajeAmable {
  return MENSAJES[motivo];
}

/** Traduce la excepcion de `getUserMedia` a un motivo del dominio. */
function _motivoDeExcepcion(error: unknown): MotivoFalloAudio {
  const nombre = (error as { name?: string } | undefined)?.name ?? '';
  switch (nombre) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'permiso_denegado';
    case 'SecurityError':
      // Chrome usa SecurityError cuando la pagina no es de confianza.
      return window.isSecureContext === false ? 'contexto_inseguro' : 'permiso_denegado';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'sin_microfono';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'microfono_ocupado';
    default:
      return 'desconocido';
  }
}

// ---------------------------------------------------------------------------
// Remuestreo a 16 kHz
// ---------------------------------------------------------------------------

/** Estado del remuestreador entre bloques (para no cortar en cada frontera). */
interface EstadoRemuestreo {
  /** Posicion fraccionaria dentro del bloque actual; puede ser negativa. */
  posicion: number;
  /** Ultima muestra del bloque anterior, para interpolar en la frontera. */
  ultima: number;
}

/**
 * Remuestrea un bloque de float32 a `FRECUENCIA_DESTINO` por interpolacion
 * lineal, arrastrando el desfase entre bloques.
 *
 * Interpolar (en vez de coger una muestra de cada N) evita el aliasing chillon
 * que confunde a faster-whisper con las eses y las efes.
 *
 * Args:
 *     bloque: muestras en [-1, 1] a la frecuencia de origen.
 *     ratio: frecuencia de origen dividida por 16.000.
 *     st: estado que persiste entre llamadas.
 *
 * Returns:
 *     Las muestras ya a 16 kHz.
 */
function _remuestrear(bloque: Float32Array, ratio: number, st: EstadoRemuestreo): Float32Array {
  const n = bloque.length;
  if (n === 0) return new Float32Array(0);

  // Primera pasada: cuantas muestras salen (los bloques son de 128 o 4.096).
  let cuenta = 0;
  for (let p = st.posicion; p <= n - 1; p += ratio) cuenta += 1;

  const salida = new Float32Array(cuenta);
  let p = st.posicion;
  for (let k = 0; k < cuenta; k += 1) {
    const i = Math.floor(p);
    const frac = p - i;
    const a = i < 0 ? st.ultima : bloque[i]!;
    const b = i + 1 < n ? bloque[i + 1]! : bloque[n - 1]!;
    salida[k] = a + (b - a) * frac;
    p += ratio;
  }

  st.posicion = p - n;
  st.ultima = bloque[n - 1]!;
  return salida;
}

/** Convierte una muestra float [-1, 1] a entero de 16 bits con signo. */
function _aEntero16(muestra: number): number {
  const s = muestra < -1 ? -1 : muestra > 1 ? 1 : muestra;
  return s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
}

/** Volumen eficaz (RMS) de un bloque. */
function _rms(bloque: Float32Array): number {
  if (bloque.length === 0) return 0;
  let suma = 0;
  for (let i = 0; i < bloque.length; i += 1) suma += bloque[i]! * bloque[i]!;
  return Math.sqrt(suma / bloque.length);
}

// ---------------------------------------------------------------------------
// Codigo del AudioWorklet
// ---------------------------------------------------------------------------

/**
 * Procesador que corre en el hilo de audio. Va como texto porque se carga
 * desde un Blob: asi el modulo es autocontenido y no hay que servir un .js
 * suelto desde `public/` (que en el kiosco es un fichero mas que perder).
 */
const FUENTE_WORKLET = `
class ProcesadorCaptura extends AudioWorkletProcessor {
  process(entradas) {
    const canal = entradas[0] && entradas[0][0];
    if (canal && canal.length > 0) {
      const copia = new Float32Array(canal);
      this.port.postMessage(copia, [copia.buffer]);
    }
    return true;
  }
}
registerProcessor('${NOMBRE_WORKLET}', ProcesadorCaptura);
`;

// ---------------------------------------------------------------------------
// Estado del modulo (una sola captura viva por kiosco)
// ---------------------------------------------------------------------------

/** Recursos del navegador que hay que soltar si o si al parar. */
interface Recursos {
  flujo: MediaStream;
  contexto: AudioContext;
  fuente: MediaStreamAudioSourceNode;
  nodo: AudioWorkletNode | ScriptProcessorNode;
  sumidero: GainNode;
}

let _recursos: Recursos | null = null;
/**
 * Generacion de la captura en curso.
 *
 * `iniciarCaptura` tiene DOS esperas (permiso del microfono y montaje del
 * grafo de audio). Si mientras tanto alguien llama a `detenerCaptura` —pasa
 * cada vez que el canal de voz se reconecta, y ahora que reconecta solo pasa
 * mas— el arranque terminaba igualmente y dejaba `_estado = 'escuchando'` con
 * `_recursos` ya a null: micrófono muerto, y el guard de entrada rechazando
 * todo reintento por creerse que ya estaba escuchando. Sintoma exacto que
 * reporto el jefe (04/08/2026): "el microfono no funciona, y al recargar si".
 *
 * Cada arranque se queda con su numero; si al volver de una espera el numero
 * ya no es el suyo, ese arranque se retira sin tocar el estado global.
 */
let _generacion = 0;
let _estado: EstadoCaptura = 'parada';
let _nivel = 0;

/** Callbacks vivos; se anulan al parar para que no llegue nada tardio. */
let _alFragmento: AlFragmento | null = null;
let _alSilencio: AlSilencio | null = null;
let _alNivel: AlNivel | null = null;

/** Parametros de la deteccion de silencio, resueltos al iniciar. */
let _msSilencioFinTurno = 1_200;
let _msVozMinima = 250;
let _umbralMinimo = 0.01;
/**
 * Tope duro de un turno hablado, en ms de VOZ acumulada.
 *
 * Con ruido de fondo constante puede no haber nunca una pausa clara. Sin este
 * tope el microfono se queda abierto indefinidamente: los turnos de 21, 42 y
 * 60 segundos del 10/09/2026 salieron de ahi, y con ellos las frases que
 * Whisper se invento para rellenar tanto silencio.
 */
let _msMaximoTurno = 20_000;

/** Acumulador de muestras hasta completar un fragmento de 100 ms. */
let _fragmento = new Int16Array(MUESTRAS_POR_FRAGMENTO);
let _usadas = 0;

/** Estado del remuestreador. */
let _remuestreo: EstadoRemuestreo = { posicion: 0, ultima: 0 };

/**
 * Lo que el detector sabe del turno en curso.
 *
 * `pisoRuido` es el ruido de fondo de la sala; sobrevive a los turnos porque
 * la sala no cambia entre una frase y la siguiente.
 */
export interface VentanaVoz {
  msVoz: number;
  msSilencio: number;
  hablando: boolean;
  pisoRuido: number;
  /**
   * Volumenes de los ultimos segundos, para estimar el ruido de la sala.
   *
   * El suelo se calcula como el MINIMO de esta ventana, que es como lo hacen
   * los detectores de voz de verdad ("minimum statistics"). Un promedio no
   * vale: al hablar seguido lo arrastra la propia voz. El minimo no, porque
   * entre palabra y palabra siempre hay un hueco que baja al nivel de la sala
   * —y si de verdad no lo hay, para eso esta el tope duro—.
   */
  recientes: number[];
}

/** Umbrales con los que se decide. */
export interface AjustesDeteccion {
  msSilencioFinTurno: number;
  msVozMinima: number;
  umbralMinimo: number;
  msMaximoTurno: number;
}

/**
 * Cuantos bloques de 100 ms se miran para estimar el ruido de la sala.
 *
 * Cuatro segundos: suficiente para que entre alguna pausa natural del habla, y
 * corto para que el suelo persiga a la sala si el ruido cambia.
 */
const BLOQUES_RUIDO = 40;

/**
 * Cuanto rato seguido "oyendo voz", sin una sola pausa, deja de ser creible.
 *
 * Ocho segundos hablando sin respirar no los hace nadie, y menos un senor de
 * 88 anos. Pasado eso se asume que el umbral esta mal calibrado y se recalcula
 * con el ruido real de la sala.
 */
const MS_SOSPECHA_SIN_PAUSA = 8_000;

/** Ventana en blanco, con el ruido de sala que se quiera arrastrar. */
export function ventanaLimpia(pisoRuido = 0.005, recientes: number[] = []): VentanaVoz {
  return { msVoz: 0, msSilencio: 0, hablando: false, pisoRuido, recientes };
}

/**
 * Decide, bloque a bloque, si el jefe ha terminado de hablar.
 *
 * Es una funcion PURA a proposito: recibe la ventana y devuelve la siguiente.
 * Asi se puede probar el caso que fallaba sin montar el grafo de audio del
 * navegador, que es lo que impedia tener pruebas de esto.
 *
 * El arreglo del 10/09/2026 esta en la primera linea del cuerpo: **el suelo de
 * ruido se aprende SIEMPRE**, no solo en los tramos callados. Antes solo se
 * actualizaba por debajo del umbral, y eso tiene una trampa: si el ruido de la
 * sala sube por encima —un ventilador, la calle, el propio altavoz del
 * kiosco—, todo pasa a contar como voz, el silencio no se acumula nunca y EL
 * TURNO NO TERMINA. El jefe acababa pulsando "Ya esta, contesteme" con veinte,
 * cuarenta o sesenta segundos de audio casi mudo dentro, y Whisper rellenaba
 * ese silencio con sus coletillas: asi aparecio en pantalla un
 * "Gracias, Androcles." que el jefe nunca dijo.
 *
 * Args:
 *     rms: volumen del bloque.
 *     msBloque: cuanto dura el bloque.
 *     ventana: estado del turno hasta ahora.
 *     ajustes: umbrales.
 *
 * Returns:
 *     La ventana siguiente y si toca cerrar el turno.
 */
export function decidirFinDeTurno(
  rms: number,
  msBloque: number,
  ventana: VentanaVoz,
  ajustes: AjustesDeteccion,
): { ventana: VentanaVoz; finDeTurno: boolean } {
  const recientes = [...ventana.recientes, rms].slice(-BLOQUES_RUIDO);
  const umbral = Math.max(ajustes.umbralMinimo, ventana.pisoRuido * 3.5);
  const siguiente: VentanaVoz = { ...ventana, recientes };

  if (rms >= umbral) {
    siguiente.msVoz += msBloque;
    siguiente.msSilencio = 0;
    if (siguiente.msVoz >= ajustes.msVozMinima) siguiente.hablando = true;

    // AQUI esta el arreglo del 10/09/2026.
    //
    // Llevamos demasiado rato "oyendo voz" sin UNA SOLA pausa. Nadie habla
    // asi: lo que se esta oyendo es la sala —un ventilador, la calle, el
    // propio altavoz del kiosco— por encima de un suelo de ruido que se
    // calibro cuando habia menos ruido. Antes eso no se detectaba nunca: el
    // silencio no se acumulaba, el turno no terminaba, y el jefe acababa
    // pulsando el boton con 20, 40 o 60 segundos de audio casi mudo dentro
    // que Whisper rellenaba con coletillas ("Gracias, Androcles.").
    //
    // Se recalibra al minimo de los ultimos segundos, que es el nivel real de
    // la sala, y a partir de ahi el ruido vuelve a ser silencio.
    if (siguiente.msVoz >= MS_SOSPECHA_SIN_PAUSA) {
      siguiente.pisoRuido = Math.min(...recientes);
    }

    // Tope duro. Aun recalibrando puede no llegar una pausa (una television
    // encendida, por ejemplo): pasado este tiempo se cierra igual. Cortar una
    // frase larga es molesto; dejar el microfono abierto un minuto es lo que
    // produce las frases inventadas.
    if (siguiente.hablando && siguiente.msVoz >= ajustes.msMaximoTurno) {
      return {
        ventana: ventanaLimpia(siguiente.pisoRuido, recientes),
        finDeTurno: true,
      };
    }
    return { ventana: siguiente, finDeTurno: false };
  }

  // Silencio de verdad: el suelo persigue al nivel de la sala. Baja deprisa
  // (la sala se ha quedado quieta) y sube despacio (no vaya a ser una pausa
  // corta entre dos palabras).
  siguiente.pisoRuido =
    rms < ventana.pisoRuido
      ? ventana.pisoRuido * 0.7 + rms * 0.3
      : ventana.pisoRuido * 0.95 + rms * 0.05;

  if (!siguiente.hablando) {
    // Un portazo no es un turno: sin voz suficiente, no hay nada que cerrar.
    siguiente.msVoz = 0;
    return { ventana: siguiente, finDeTurno: false };
  }

  siguiente.msSilencio += msBloque;
  if (siguiente.msSilencio < ajustes.msSilencioFinTurno) {
    return { ventana: siguiente, finDeTurno: false };
  }
  return { ventana: ventanaLimpia(siguiente.pisoRuido, recientes), finDeTurno: true };
}

/** Ventana de deteccion de voz/silencio del turno en curso. */
let _ventana: VentanaVoz = ventanaLimpia();
let _ultimoNivelEmitido = 0;

/** Reinicia los contadores del turno, conservando el ruido aprendido. */
function _reiniciarVentana(): void {
  _ventana = ventanaLimpia(_ventana.pisoRuido);
}

/** Vacia el acumulador enviando lo que haya (cola del turno). */
function _vaciarFragmento(): void {
  if (_usadas === 0 || !_alFragmento) {
    _usadas = 0;
    return;
  }
  const cola = new Int16Array(_usadas);
  cola.set(_fragmento.subarray(0, _usadas));
  _usadas = 0;
  _alFragmento(cola.buffer);
}

/**
 * Procesa un bloque crudo del microfono: nivel, remuestreo, troceo y silencio.
 *
 * Args:
 *     bloque: muestras float32 del canal izquierdo.
 *     frecuenciaOrigen: frecuencia real del `AudioContext`.
 */
function _procesarBloque(bloque: Float32Array, frecuenciaOrigen: number): void {
  // 'iniciando' cuenta: el contexto empieza a bombear en cuanto se conecta el
  // grafo, un pelo antes de que `iniciarCaptura` marque 'escuchando'. Sin esto
  // se perderian las primeras decimas de la primera palabra.
  if ((_estado !== 'escuchando' && _estado !== 'iniciando') || bloque.length === 0) return;

  const rms = _rms(bloque);
  const msBloque = (bloque.length / frecuenciaOrigen) * 1_000;

  // --- indicador visual -------------------------------------------------
  // Raiz cuadrada: el oido (y el ojo) perciben el volumen de forma logaritmica;
  // sin esto la barra parece muerta con una voz normal.
  const objetivo = Math.min(1, Math.sqrt(rms / 0.2));
  _nivel = _nivel * 0.6 + objetivo * 0.4;
  const ahora = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (_alNivel && ahora - _ultimoNivelEmitido >= MS_ENTRE_NIVELES) {
    _ultimoNivelEmitido = ahora;
    _alNivel(_nivel);
  }

  // --- troceo a 16 kHz --------------------------------------------------
  const ratio = frecuenciaOrigen / FRECUENCIA_DESTINO;
  const remuestreado = _remuestrear(bloque, ratio, _remuestreo);
  for (let i = 0; i < remuestreado.length; i += 1) {
    _fragmento[_usadas] = _aEntero16(remuestreado[i]!);
    _usadas += 1;
    if (_usadas === MUESTRAS_POR_FRAGMENTO) {
      const lleno = _fragmento;
      _fragmento = new Int16Array(MUESTRAS_POR_FRAGMENTO);
      _usadas = 0;
      // `.buffer` es un ArrayBuffer nuevo en cada vuelta: el WebSocket puede
      // quedarselo sin que el siguiente fragmento lo pise.
      _alFragmento?.(lleno.buffer);
    }
  }

  // --- deteccion de silencio -------------------------------------------
  const decision = decidirFinDeTurno(rms, msBloque, _ventana, {
    msSilencioFinTurno: _msSilencioFinTurno,
    msVozMinima: _msVozMinima,
    umbralMinimo: _umbralMinimo,
    msMaximoTurno: _msMaximoTurno,
  });
  _ventana = decision.ventana;
  if (!decision.finDeTurno) return;

  // Fin de turno: primero la cola de audio, despues el aviso. Al reves el
  // backend transcribiria un turno al que le falta la ultima silaba.
  _reiniciarVentana();
  _vaciarFragmento();
  _alSilencio?.();
}

// ---------------------------------------------------------------------------
// API publica
// ---------------------------------------------------------------------------

/**
 * Abre el microfono y empieza a mandar PCM de 16 kHz.
 *
 * Es idempotente: si ya se esta capturando no hace nada. Si falla, deja todo
 * cerrado y lanza `ErrorAudio` con un mensaje que se puede pintar tal cual.
 *
 * Args:
 *     alFragmento: recibe cada 100 ms de PCM listo para el WebSocket.
 *     alSilencio: se llama cuando el turno ha terminado por silencio.
 *     opciones: nivel visual, avisos de fallo y ajustes de la ventana.
 *
 * Raises:
 *     ErrorAudio: sin permiso, sin microfono, o pagina no confiable.
 */
export async function iniciarCaptura(
  alFragmento: AlFragmento,
  alSilencio: AlSilencio,
  opciones: OpcionesCaptura = {},
): Promise<void> {
  // Estado imposible: nos creemos escuchando pero no hay grafo de audio. Si
  // alguna carrera dejara esto asi otra vez, el kiosco se cura solo en vez de
  // quedarse sordo hasta que alguien recargue la pagina.
  if (_estado === 'escuchando' && !_recursos) detenerCaptura();

  if (_estado === 'escuchando' || _estado === 'iniciando') return;

  const miGeneracion = ++_generacion;

  const fallo = diagnosticarMicrofono();
  if (fallo) {
    _estado = 'error';
    throw new ErrorAudio(fallo);
  }

  _estado = 'iniciando';
  _alFragmento = alFragmento;
  _alSilencio = alSilencio;
  _alNivel = opciones.alNivel ?? null;
  _msSilencioFinTurno = opciones.msSilencioFinTurno ?? 1_200;
  _msVozMinima = opciones.msVozMinima ?? 250;
  _umbralMinimo = opciones.umbralMinimo ?? 0.01;
  _msMaximoTurno = opciones.msMaximoTurno ?? 20_000;
  _fragmento = new Int16Array(MUESTRAS_POR_FRAGMENTO);
  _usadas = 0;
  _remuestreo = { posicion: 0, ultima: 0 };
  _nivel = 0;
  _ultimoNivelEmitido = 0;
  // Sala nueva: se vuelve a aprender el ruido desde cero.
  _ventana = ventanaLimpia();

  let flujo: MediaStream;
  try {
    flujo = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        // Cancelacion de eco imprescindible: el kiosco tiene el altavoz al
        // lado del microfono y Androcles se oiria a si mismo.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
  } catch (error) {
    _limpiar();
    _estado = 'error';
    throw new ErrorAudio(_motivoDeExcepcion(error), error);
  }

  // Han parado la captura mientras se pedia el permiso: se suelta el flujo
  // recien concedido y se sale SIN tocar el estado (lo manda quien paro).
  if (miGeneracion !== _generacion) {
    flujo.getTracks().forEach((pista) => pista.stop());
    return;
  }

  try {
    await _montarGrafo(flujo, opciones);
  } catch (error) {
    flujo.getTracks().forEach((pista) => pista.stop());
    _limpiar();
    _estado = 'error';
    throw new ErrorAudio('desconocido', error);
  }

  // Igual que antes, pero tras montar el grafo: si ya no somos la captura
  // vigente, se desmonta lo recien creado en vez de marcarnos 'escuchando'.
  if (miGeneracion !== _generacion) {
    detenerCaptura();
    return;
  }

  _estado = 'escuchando';
}

/**
 * Monta la cadena de nodos de audio sobre un flujo ya concedido.
 *
 * Args:
 *     flujo: `MediaStream` del microfono.
 *     opciones: para avisar si el microfono se cae en caliente.
 */
async function _montarGrafo(flujo: MediaStream, opciones: OpcionesCaptura): Promise<void> {
  // Pedir ya 16 kHz ahorra trabajo, pero varios navegadores lo ignoran o
  // lanzan: el remuestreo de abajo funciona con la frecuencia que salga.
  const CtorAudio: typeof AudioContext =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  let contexto: AudioContext;
  try {
    contexto = new CtorAudio({ sampleRate: FRECUENCIA_DESTINO });
  } catch {
    contexto = new CtorAudio();
  }

  const fuente = contexto.createMediaStreamSource(flujo);
  const sumidero = contexto.createGain();
  // Sumidero mudo: el grafo solo se procesa si llega al destino, pero el jefe
  // NO debe oirse a si mismo por el altavoz.
  sumidero.gain.value = 0;

  let nodo: AudioWorkletNode | ScriptProcessorNode;
  const frecuencia = contexto.sampleRate;

  const hayWorklet =
    typeof AudioWorkletNode !== 'undefined' &&
    typeof contexto.audioWorklet?.addModule === 'function';

  if (hayWorklet) {
    const url = URL.createObjectURL(new Blob([FUENTE_WORKLET], { type: 'text/javascript' }));
    try {
      await contexto.audioWorklet.addModule(url);
    } finally {
      URL.revokeObjectURL(url);
    }
    const worklet = new AudioWorkletNode(contexto, NOMBRE_WORKLET, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
    });
    worklet.port.onmessage = (evento: MessageEvent<Float32Array>) => {
      _procesarBloque(evento.data, frecuencia);
    };
    nodo = worklet;
  } else {
    // Respaldo obsoleto pero universal.
    const script = contexto.createScriptProcessor(BLOQUE_RESPALDO, 1, 1);
    script.onaudioprocess = (evento) => {
      _procesarBloque(evento.inputBuffer.getChannelData(0), frecuencia);
    };
    nodo = script;
  }

  fuente.connect(nodo);
  nodo.connect(sumidero);
  sumidero.connect(contexto.destination);

  // El navegador puede abrir el contexto suspendido (politica de autoplay).
  if (contexto.state === 'suspended') await contexto.resume();

  // El microfono se puede desenchufar con el kiosco encendido.
  const pista = flujo.getAudioTracks()[0];
  if (pista) {
    pista.onended = () => {
      detenerCaptura();
      _estado = 'error';
      opciones.alFallo?.(new ErrorAudio('sin_microfono'));
    };
  }

  _recursos = { flujo, contexto, fuente, nodo, sumidero };
}

/** Suelta callbacks y contadores sin tocar los nodos (ya cerrados o nunca abiertos). */
function _limpiar(): void {
  _alFragmento = null;
  _alSilencio = null;
  _alNivel = null;
  _usadas = 0;
  _nivel = 0;
  _reiniciarVentana();
}

/**
 * Cierra el microfono y libera todo.
 *
 * Se puede llamar siempre, incluso sin captura abierta: es el camino de salida
 * de los `useEffect` y de cualquier fallo.
 */
export function detenerCaptura(): void {
  // Invalida cualquier arranque a medias: cuando vuelva de su espera vera que
  // ya no es el vigente y se retirara solo.
  _generacion += 1;
  const recursos = _recursos;
  _recursos = null;
  _estado = 'parada';
  _limpiar();

  if (!recursos) return;

  try {
    if ('port' in recursos.nodo) {
      recursos.nodo.port.onmessage = null;
    } else {
      recursos.nodo.onaudioprocess = null;
    }
  } catch {
    // Nodo ya invalidado: nada que hacer.
  }

  for (const nodo of [recursos.fuente, recursos.nodo, recursos.sumidero]) {
    try {
      nodo.disconnect();
    } catch {
      // Desconectar dos veces lanza en algunos navegadores; es inofensivo.
    }
  }

  recursos.flujo.getTracks().forEach((pista) => {
    pista.onended = null;
    pista.stop();
  });

  // Cerrar el contexto es lo que apaga de verdad el piloto del microfono.
  void recursos.contexto.close().catch(() => undefined);
}

/** Estado actual de la captura. */
export function estado(): EstadoCaptura {
  return _estado;
}

/** Ultimo volumen medido (0..1). Util para pintar sin esperar al callback. */
export function nivel(): number {
  return _nivel;
}
